import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Card, StatusIndicator, type StatusTone } from "./ui";
import { StateTag } from "./phase3";
import {
  ONBOARDING_MODES,
  type ManagedMessageStatus,
  type ManagedMessageType,
  type OnboardingBehavior,
  type OnboardingConfigResponse,
  type OnboardingMessageContent,
  type OnboardingMessageSlot,
  type OnboardingMode,
  type OnboardingModuleContent,
  type OnboardingPlanPreview,
  type OnboardingStatusView,
  type OnboardingValidationOutcome,
  type OnboardingVersionView,
  type RoleMenuContent,
  type RoleMenuFinding,
  type RoleMenuOptionContent,
  type RoleMenuType,
} from "../lib/api";
import { formatDateTime } from "../lib/format";

export const MESSAGE_SLOT_LABEL: Record<OnboardingMessageSlot, string> = {
  welcome: "Welcome message",
  rulesAck: "Rules acknowledgement",
  verificationPrompt: "Verification prompt",
  verificationSuccess: "Verification success",
  verificationFailure: "Verification failure",
};

/**
 * Presentational + pure building blocks for the Phase 6A community-onboarding
 * surfaces. Everything here is deliberately data-fetch-free so it can be unit
 * tested with mock data, and every "preview" is representative — clearly not a
 * live, interactive Discord control.
 *
 * SAFETY LANGUAGE (used verbatim across these surfaces):
 *  - drafts / dry-runs:      "No Discord changes have been made."
 *  - deployment gate:        "A human approval is required before deployment."
 *  - what verification means: "member verification" / "rules acknowledgement" /
 *                             "server access confirmation" — never identity proof.
 */
export const NO_DISCORD_CHANGES = "No Discord changes have been made.";
export const HUMAN_APPROVAL_REQUIRED = "A human approval is required before deployment.";
export const PREVIEW_DISCLAIMER = "This preview is representative — it is not pixel-perfect across Discord clients.";
export const VERIFICATION_FRAMING =
  "Button verification is a member-verification / rules-acknowledgement / server-access-confirmation step. It does not prove a member's real-world identity and does not block all bots.";

// ── Mode metadata ────────────────────────────────────────────────────────────
export const MODE_META: Record<OnboardingMode, { label: string; blurb: string }> = {
  INFORMATION_ONLY: {
    label: "Information only",
    blurb: "Post welcome / rules messages. No verification, no role changes.",
  },
  RULES_ACKNOWLEDGEMENT: {
    label: "Rules acknowledgement",
    blurb: "Members acknowledge the rules. This is an acknowledgement, not identity proof.",
  },
  BUTTON_VERIFICATION: {
    label: "Button verification",
    blurb: "Members confirm server access by pressing a button. Member verification only — it does not prove identity or block all bots.",
  },
  MANUAL_APPROVAL: {
    label: "Manual approval",
    blurb: "A human reviewer approves each member before the verified role is granted.",
  },
  HYBRID: {
    label: "Hybrid",
    blurb: "Rules acknowledgement plus button verification, with optional manual review.",
  },
};

// ── Character limits (mirror the server module schema) ───────────────────────
export const MESSAGE_LIMITS = {
  plainText: 2000,
  title: 256,
  description: 4096,
  fieldName: 256,
  fieldValue: 1024,
  footer: 2048,
  fields: 25,
} as const;

export interface ContentFinding {
  severity: "blocker" | "warning" | "info";
  message: string;
}

function over(value: string | undefined, limit: number): boolean {
  return (value?.length ?? 0) > limit;
}

/**
 * Analyse a message's content for character-limit overflows, allowed-mention
 * risks, and (when a known-key set is supplied) unresolved role references. Pure.
 */
export function analyzeMessageContent(
  content: OnboardingMessageContent,
  opts: { knownRoleKeys?: ReadonlySet<string> } = {},
): ContentFinding[] {
  const findings: ContentFinding[] = [];
  const limitCheck: [string, string | undefined, number][] = [
    ["Plain text", content.plainText, MESSAGE_LIMITS.plainText],
    ["Title", content.title, MESSAGE_LIMITS.title],
    ["Description", content.description, MESSAGE_LIMITS.description],
    ["Footer", content.footer, MESSAGE_LIMITS.footer],
  ];
  for (const [label, value, limit] of limitCheck) {
    if (over(value, limit)) {
      findings.push({ severity: "blocker", message: `${label} is ${value?.length} characters — over the ${limit} limit. Discord would reject it.` });
    }
  }
  const fields = content.fields ?? [];
  if (fields.length > MESSAGE_LIMITS.fields) {
    findings.push({ severity: "blocker", message: `${fields.length} embed fields — over the ${MESSAGE_LIMITS.fields} limit.` });
  }
  fields.forEach((f, i) => {
    if (over(f.name, MESSAGE_LIMITS.fieldName)) findings.push({ severity: "blocker", message: `Field ${i + 1} name is over the ${MESSAGE_LIMITS.fieldName} limit.` });
    if (over(f.value, MESSAGE_LIMITS.fieldValue)) findings.push({ severity: "blocker", message: `Field ${i + 1} value is over the ${MESSAGE_LIMITS.fieldValue} limit.` });
  });

  const mentions = content.allowedMentions;
  if (mentions?.everyone) {
    findings.push({ severity: "warning", message: "@everyone / @here mentions are allowed — this will ping the whole server when posted." });
  }
  if (mentions?.users) {
    findings.push({ severity: "warning", message: "User mentions are allowed — individual members may be pinged." });
  }
  for (const roleKey of mentions?.roles ?? []) {
    if (opts.knownRoleKeys && !opts.knownRoleKeys.has(roleKey)) {
      findings.push({ severity: "warning", message: `Allowed-mention role "${roleKey}" is not declared in the blueprint — the reference is unresolved.` });
    } else {
      findings.push({ severity: "info", message: `Role "${roleKey}" is allowed to be mentioned.` });
    }
  }
  return findings;
}

/** Representative (disabled) components ODIN would render for a message type. */
export function representativeComponents(type: ManagedMessageType): { label: string; tone: StatusTone }[] {
  switch (type) {
    case "RULES":
      return [{ label: "I acknowledge the rules", tone: "ok" }];
    case "VERIFICATION":
      return [{ label: "Verify my access", tone: "ok" }];
    case "ROLE_MENU":
      return [{ label: "Select roles…", tone: "neutral" }];
    default:
      return [];
  }
}

export function findingTone(severity: ContentFinding["severity"] | RoleMenuFinding["severity"]): StatusTone {
  if (severity === "blocker" || severity === "BLOCKER") return "danger";
  if (severity === "warning" || severity === "WARNING") return "warn";
  return "neutral";
}

// ── Character-limit indicator ────────────────────────────────────────────────
export function CharCount({ value, limit, label }: { value: string | undefined; limit: number; label: string }): JSX.Element {
  const used = value?.length ?? 0;
  const isOver = used > limit;
  return (
    <span
      className={`ob-charcount${isOver ? " ob-charcount--over" : ""}`}
      role="status"
      aria-label={`${label}: ${used} of ${limit} characters${isOver ? ", over limit" : ""}`}
    >
      {label}: {used}/{limit}
    </span>
  );
}

// ── Managed-message status mapping ───────────────────────────────────────────
export type MessageHealth = "HEALTHY" | "WARNING" | "DEGRADED" | "MISSING" | "OUTDATED";

export function messageHealth(status: ManagedMessageStatus): MessageHealth {
  switch (status) {
    case "ACTIVE":
      return "HEALTHY";
    case "DEGRADED":
      return "DEGRADED";
    case "MISSING":
    case "REVOKED":
      return "MISSING";
    case "OUTDATED":
      return "OUTDATED";
    case "PLANNED":
      return "WARNING";
    default:
      return "WARNING";
  }
}

export function healthTone(h: MessageHealth): StatusTone {
  switch (h) {
    case "HEALTHY":
      return "ok";
    case "OUTDATED":
    case "WARNING":
      return "warn";
    case "DEGRADED":
    case "MISSING":
      return "danger";
    default:
      return "neutral";
  }
}

export function ManagedMessageStatusPill({ status }: { status: ManagedMessageStatus }): JSX.Element {
  const h = messageHealth(status);
  return <StatusIndicator tone={healthTone(h)} label={`${h} (${status})`} />;
}

// ── Discord-style message preview (representative, never interactive) ─────────
export function MessagePreview({
  messageType,
  content,
  botName = "Your ODIN bot",
  knownRoleKeys,
  width = "responsive",
}: {
  messageType: ManagedMessageType;
  content: OnboardingMessageContent;
  botName?: string;
  knownRoleKeys?: ReadonlySet<string>;
  width?: "responsive" | "mobile" | "desktop";
}): JSX.Element {
  const findings = analyzeMessageContent(content, { knownRoleKeys });
  const components = representativeComponents(messageType);
  const hasEmbed = Boolean(content.title || content.description || (content.fields?.length ?? 0) > 0 || content.footer);

  return (
    <div className={`ob-preview ob-preview--${width}`}>
      <p className="ob-preview__disclaimer">
        {PREVIEW_DISCLAIMER} It is not interactive — {NO_DISCORD_CHANGES}
      </p>
      <div className="ob-msg" role="group" aria-label={`Preview of the ${messageType.toLowerCase()} message`}>
        <div className="ob-msg__avatar" aria-hidden="true" />
        <div className="ob-msg__body">
          <div className="ob-msg__head">
            <span className="ob-msg__author">{botName}</span>
            <span className="ob-msg__tag">APP</span>
            <span className="ob-msg__time">preview</span>
          </div>
          {content.plainText ? <p className="ob-msg__text">{content.plainText}</p> : null}
          {hasEmbed ? (
            <div className="ob-embed">
              {content.title ? <p className="ob-embed__title">{content.title}</p> : null}
              {content.description ? <p className="ob-embed__desc">{content.description}</p> : null}
              {(content.fields ?? []).length > 0 ? (
                <div className="ob-embed__fields">
                  {(content.fields ?? []).map((f, i) => (
                    <div key={i} className="ob-embed__field">
                      <p className="ob-embed__field-name">{f.name}</p>
                      <p className="ob-embed__field-value">{f.value}</p>
                    </div>
                  ))}
                </div>
              ) : null}
              {content.footer ? <p className="ob-embed__footer">{content.footer}</p> : null}
            </div>
          ) : null}
          {components.length > 0 ? (
            <div className="ob-msg__components" role="group" aria-label="Representative components (not interactive)">
              {components.map((c) => (
                <button key={c.label} type="button" className={`btn btn--secondary ob-fake-btn`} disabled aria-disabled="true" title="Preview only — not interactive">
                  {c.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="ob-preview__meters" aria-label="Character-limit indicators">
        <CharCount label="Text" value={content.plainText} limit={MESSAGE_LIMITS.plainText} />
        {content.title !== undefined ? <CharCount label="Title" value={content.title} limit={MESSAGE_LIMITS.title} /> : null}
        {content.description !== undefined ? <CharCount label="Desc" value={content.description} limit={MESSAGE_LIMITS.description} /> : null}
        {content.footer !== undefined ? <CharCount label="Footer" value={content.footer} limit={MESSAGE_LIMITS.footer} /> : null}
      </div>

      <FindingList findings={findings} emptyLabel="No content warnings." />
    </div>
  );
}

/** Side-by-side current-vs-proposed message compare. */
export function MessageCompare({
  messageType,
  current,
  proposed,
  knownRoleKeys,
}: {
  messageType: ManagedMessageType;
  current: OnboardingMessageContent | null;
  proposed: OnboardingMessageContent | null;
  knownRoleKeys?: ReadonlySet<string>;
}): JSX.Element {
  return (
    <div className="ob-compare">
      <div className="ob-compare__col">
        <StateTag state="current" />
        {current ? <MessagePreview messageType={messageType} content={current} knownRoleKeys={knownRoleKeys} /> : <p className="muted">Not deployed yet.</p>}
      </div>
      <div className="ob-compare__col">
        <StateTag state="proposed" />
        {proposed ? <MessagePreview messageType={messageType} content={proposed} knownRoleKeys={knownRoleKeys} /> : <p className="muted">No proposed content.</p>}
      </div>
    </div>
  );
}

export function FindingList({ findings, emptyLabel }: { findings: ContentFinding[]; emptyLabel: string }): JSX.Element {
  if (findings.length === 0) return <p className="muted">{emptyLabel}</p>;
  return (
    <ul className="ob-findings">
      {findings.map((f, i) => (
        <li key={i} className={`ob-finding ob-finding--${findingTone(f.severity)}`}>
          <span className={`sev sev--${findingTone(f.severity)}`}>{f.severity.toUpperCase()}</span> {f.message}
        </li>
      ))}
    </ul>
  );
}

// ── Role-menu preview + structural validation ────────────────────────────────
export function roleMenuComponentCount(menu: RoleMenuContent): number {
  // TOGGLE_BUTTONS renders one button per option; selects render a single component.
  return menu.menuType === "TOGGLE_BUTTONS" ? menu.options.length : 1;
}

/**
 * Structural role-menu validation (the subset checkable without live role facts).
 * Privilege / hierarchy / managed-role blocks are added server-side by the
 * validate endpoint against a snapshot — this only catches shape problems early.
 */
export function validateRoleMenuDraft(menu: RoleMenuContent): RoleMenuFinding[] {
  const findings: RoleMenuFinding[] = [];
  const block = (code: string, message: string, roleKey?: string) => findings.push({ code, severity: "BLOCKER", menuId: menu.id || "(new)", message, ...(roleKey ? { roleKey } : {}) });
  const warn = (code: string, message: string) => findings.push({ code, severity: "WARNING", menuId: menu.id || "(new)", message });

  if (!menu.id.trim()) block("MISSING_ID", "The menu needs a stable id.");
  if (!menu.title.trim()) block("MISSING_TITLE", "The menu needs a title.");
  if (!menu.channelKey.trim()) block("MISSING_CHANNEL", "Select a target channel (by resource key).");
  if (menu.options.length < 1) block("NO_OPTIONS", "Add at least one option.");
  if (menu.options.length > 25) block("TOO_MANY_OPTIONS", "A menu may have at most 25 options.");

  const min = menu.minSelections ?? 0;
  const max = menu.maxSelections ?? 25;
  if (min > max) block("INVALID_SELECTION_COUNT", "Minimum selections exceeds maximum selections.");

  const seen = new Set<string>();
  const groups = new Set<string>();
  for (const opt of menu.options) {
    if (!opt.label.trim()) block("MISSING_LABEL", "Every option needs a label.", opt.roleKey);
    if (!opt.roleKey.trim()) block("MISSING_ROLE", "Every option needs a linked role resource key.");
    if (opt.roleKey && seen.has(opt.roleKey)) block("DUPLICATE_OPTION", `Role "${opt.roleKey}" appears more than once.`, opt.roleKey);
    if (opt.roleKey) seen.add(opt.roleKey);
    if (opt.exclusiveGroup) groups.add(opt.exclusiveGroup);
  }
  if (menu.menuType === "EXCLUSIVE_GROUP" && groups.size > 1) warn("MULTIPLE_EXCLUSIVE_GROUPS", "An exclusive-group menu mixes more than one group; selections may conflict.");
  if (menu.menuType === "SINGLE_SELECT" && max > 1) warn("SINGLE_SELECT_MAX", "Single-select menu has a maximum-selection greater than 1.");
  return findings;
}

export function RoleMenuPreview({ menu }: { menu: RoleMenuContent }): JSX.Element {
  const asButtons = menu.menuType === "TOGGLE_BUTTONS";
  const count = roleMenuComponentCount(menu);
  return (
    <div className="ob-preview ob-preview--responsive">
      <p className="ob-preview__disclaimer">
        {PREVIEW_DISCLAIMER} It is not interactive — {NO_DISCORD_CHANGES}
      </p>
      <div className="ob-msg" role="group" aria-label={`Preview of role menu ${menu.title || menu.id}`}>
        <div className="ob-msg__avatar" aria-hidden="true" />
        <div className="ob-msg__body">
          <div className="ob-msg__head">
            <span className="ob-msg__author">Your ODIN bot</span>
            <span className="ob-msg__tag">APP</span>
          </div>
          <div className="ob-embed">
            {menu.title ? <p className="ob-embed__title">{menu.title}</p> : null}
            {menu.description ? <p className="ob-embed__desc">{menu.description}</p> : null}
          </div>
          <div className={asButtons ? "ob-msg__components" : "ob-menu-select"} role="group" aria-label="Representative role controls (not interactive)">
            {asButtons ? (
              menu.options.map((o) => (
                <button key={o.roleKey || o.label} type="button" className="btn btn--secondary ob-fake-btn" disabled aria-disabled="true" title="Preview only — not interactive">
                  {o.emoji ? `${o.emoji} ` : ""}
                  {o.label || o.roleKey || "option"}
                </button>
              ))
            ) : (
              <label className="ob-menu-select__field">
                <span className="ob-menu-select__placeholder">
                  {menu.menuType === "MULTI_SELECT" ? "Select one or more…" : "Select one…"}
                </span>
                <select className="field__input" disabled aria-disabled="true" aria-label={`${menu.title || menu.id} (preview, not interactive)`}>
                  {menu.options.map((o) => (
                    <option key={o.roleKey || o.label}>{o.label || o.roleKey}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      </div>
      <div className="ob-preview__meters">
        <span className={`ob-charcount${count > 25 ? " ob-charcount--over" : ""}`}>Components: {count}/25</span>
        <span className="ob-charcount">Options: {menu.options.length}/25</span>
        {menu.requireVerified ? <span className="badge">requires verified member</span> : null}
      </div>
    </div>
  );
}

// ── Operations / status view (real record values only) ───────────────────────
export interface OpsRemediation {
  onRefresh?: () => void;
  onRegenerateDryRun?: () => void;
  onReviewFailedAssignments?: () => void;
  releaseReviewHref?: string;
  permissionsGuideHref?: string;
  rolePositionGuideHref?: string;
}

const MESSAGE_TYPE_LABEL: Record<ManagedMessageType, string> = {
  WELCOME: "Welcome",
  RULES: "Rules",
  VERIFICATION: "Verification",
  ROLE_MENU: "Role menu",
  ONBOARDING_HELP: "Onboarding help",
};

export function OnboardingOpsView({
  status,
  remediation = {},
}: {
  status: OnboardingStatusView;
  remediation?: OpsRemediation;
}): JSX.Element {
  const messages = status.managedMessages ?? [];
  const counts = useMemo(() => {
    const c: Record<MessageHealth, number> = { HEALTHY: 0, WARNING: 0, DEGRADED: 0, MISSING: 0, OUTDATED: 0 };
    for (const m of messages) c[messageHealth(m.status)] += 1;
    return c;
  }, [messages]);

  const missing = messages.filter((m) => messageHealth(m.status) === "MISSING");
  const outdated = messages.filter((m) => messageHealth(m.status) === "OUTDATED");
  const degraded = messages.filter((m) => messageHealth(m.status) === "DEGRADED");
  // Any managed message whose config version trails the active version is "stale".
  const activeVersionNumbers = messages.map((m) => m.configVersion);
  const maxVersion = activeVersionNumbers.length ? Math.max(...activeVersionNumbers) : 0;
  const staleMessages = messages.filter((m) => m.configVersion < maxVersion);

  return (
    <div className="ob-ops">
      <div className="dryrun-banner" role="status">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>These are read-only record values from ODIN's status endpoint. This page never edits, deploys to, or approves anything on Discord.</span>
      </div>

      <Card title="Onboarding status">
        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Onboarding</span>
            <span className="kv__v">
              <StatusIndicator tone={status.enabled ? "ok" : "neutral"} label={status.enabled ? "Enabled" : "Not enabled"} />
            </span>
          </div>
          <div className="kv">
            <span className="kv__k">Active version</span>
            <span className="kv__v kv__v--mono">{status.activeVersionId ?? "— none deployed —"}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Draft/version count</span>
            <span className="kv__v">{status.versionCount}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Managed messages</span>
            <span className="kv__v">{messages.length}</span>
          </div>
        </div>
      </Card>

      <Card title="Managed-message health" description="Drift is classified deterministically from the state ODIN recorded — it is never a scan of unrelated messages.">
        <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }} aria-label="Managed-message health counts">
          <span className="count-chip count-chip--ok"><strong>{counts.HEALTHY}</strong> healthy</span>
          <span className={`count-chip count-chip--${counts.OUTDATED ? "warn" : "neutral"}`}><strong>{counts.OUTDATED}</strong> outdated</span>
          <span className={`count-chip count-chip--${counts.DEGRADED ? "danger" : "neutral"}`}><strong>{counts.DEGRADED}</strong> degraded</span>
          <span className={`count-chip count-chip--${counts.MISSING ? "danger" : "neutral"}`}><strong>{counts.MISSING}</strong> missing</span>
          <span className={`count-chip count-chip--${counts.WARNING ? "warn" : "neutral"}`}><strong>{counts.WARNING}</strong> pending/other</span>
        </div>
        {messages.length === 0 ? (
          <p className="muted" style={{ marginTop: "0.75rem" }}>No managed messages recorded yet.</p>
        ) : (
          <div className="op-list" style={{ marginTop: "0.75rem" }}>
            {messages.map((m) => (
              <div key={m.id} className="list-row list-row--muted" style={{ cursor: "default" }}>
                <ManagedMessageStatusPill status={m.status} />
                <span>{MESSAGE_TYPE_LABEL[m.messageType]}</span>
                <span className="muted">channel {m.discordChannelId} · config v{m.configVersion}{m.lastVerifiedAt ? ` · verified ${formatDateTime(new Date(m.lastVerifiedAt))}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Member-level counts"
        description="Rules-acknowledgement, verification-state, pending-review and failed-assignment counts are member records that this status read does not expose."
      >
        <p className="muted">
          To keep this honest, ODIN does not fabricate per-member analytics here. Individual manual-review requests are looked up by
          request id, and role-assignment outcomes are audited server-side. The blockers below come from real dry-run / validation findings.
        </p>
      </Card>

      <Card title="Diagnostics">
        <ul className="ob-diagnostics">
          <DiagRow ok={!missing.length} label="Missing messages" detail={missing.length ? `${missing.length} managed message(s) no longer exist.` : "None missing."} />
          <DiagRow ok={!outdated.length} label="Outdated messages" detail={outdated.length ? `${outdated.length} deployed message(s) trail the current blueprint.` : "All up to date."} />
          <DiagRow ok={!degraded.length} label="Edited outside ODIN" detail={degraded.length ? `${degraded.length} message(s) were changed outside ODIN.` : "No drift detected."} />
          <DiagRow ok={!staleMessages.length} label="Stale config" detail={staleMessages.length ? `${staleMessages.length} message(s) reference an older config version.` : "Config versions aligned."} />
          <DiagRow ok label="Bot-permission blockers" detail="Reported by the dry-run and role-menu validation findings (see the wizard / builder)." neutral />
          <DiagRow ok label="Role-hierarchy blockers" detail="A role at or above the bot's highest role is a blocker in validation, not here." neutral />
        </ul>
      </Card>

      <Card title="Remediation" description="Every action here is safe: reads, previews, or a hand-off to the human-gated Phase 4 flow.">
        <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
          <button type="button" className="btn btn--secondary" onClick={remediation.onRefresh} disabled={!remediation.onRefresh}>Refresh status</button>
          <button type="button" className="btn btn--secondary" onClick={remediation.onRegenerateDryRun} disabled={!remediation.onRegenerateDryRun}>Regenerate dry run</button>
          <button type="button" className="btn btn--secondary" onClick={remediation.onReviewFailedAssignments} disabled={!remediation.onReviewFailedAssignments}>Review failed assignments</button>
          {remediation.permissionsGuideHref ? (
            <a className="btn btn--secondary" href={remediation.permissionsGuideHref} target="_blank" rel="noreferrer">Bot-permissions guide ↗</a>
          ) : null}
          {remediation.rolePositionGuideHref ? (
            <a className="btn btn--secondary" href={remediation.rolePositionGuideHref} target="_blank" rel="noreferrer">Role-position guide ↗</a>
          ) : null}
          {remediation.releaseReviewHref ? (
            <a className="btn btn--primary" href={remediation.releaseReviewHref}>Redeploy approved config (Phase 4) →</a>
          ) : null}
        </div>
        <p className="muted" style={{ marginTop: "0.75rem" }}>
          "Redeploy approved config" only opens the existing Phase 4 release review. {HUMAN_APPROVAL_REQUIRED}
        </p>
      </Card>
    </div>
  );
}

function DiagRow({ ok, label, detail, neutral = false }: { ok: boolean; label: string; detail: string; neutral?: boolean }): JSX.Element {
  return (
    <li className="ob-diagnostics__row">
      <StatusIndicator tone={neutral ? "neutral" : ok ? "ok" : "warn"} label={label} />
      <span className="muted">{detail}</span>
    </li>
  );
}

// ── Small shared field helpers reused by the wizard/builder ──────────────────
export const obInputStyle: CSSProperties = {
  padding: "0.5rem",
  borderRadius: 6,
  border: "1px solid var(--color-border-strong)",
  background: "var(--color-surface-raised)",
  color: "var(--color-text)",
  minWidth: 200,
};

export function LabeledField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }): JSX.Element {
  return (
    <label className="field" style={{ display: "grid", gap: "0.25rem" }}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="muted" style={{ fontSize: "var(--text-xs)" }}>{hint}</span> : null}
    </label>
  );
}

// ── Role-menu builder (structured — never raw component JSON) ─────────────────
export function emptyRoleMenu(): RoleMenuContent {
  return {
    id: "",
    title: "",
    description: "",
    channelKey: "",
    menuType: "MULTI_SELECT",
    minSelections: 0,
    maxSelections: 25,
    requireVerified: false,
    enabled: true,
    version: "1",
    options: [],
  };
}

const ROLE_MENU_TYPE_LABEL: Record<RoleMenuType, string> = {
  SINGLE_SELECT: "Single select (pick one)",
  MULTI_SELECT: "Multi select (pick several)",
  TOGGLE_BUTTONS: "Toggle buttons",
  EXCLUSIVE_GROUP: "Exclusive group",
};

export function RoleMenuBuilder({
  initial,
  onValidateRemote,
  onGenerateDryRun,
}: {
  initial?: RoleMenuContent;
  onValidateRemote?: (menu: RoleMenuContent) => void;
  onGenerateDryRun?: (menu: RoleMenuContent) => void;
}): JSX.Element {
  const [menu, setMenu] = useState<RoleMenuContent>(initial ?? emptyRoleMenu());
  const findings = validateRoleMenuDraft(menu);
  const blockers = findings.filter((f) => f.severity === "BLOCKER");
  const valid = blockers.length === 0;

  const patch = (p: Partial<RoleMenuContent>) => setMenu((m) => ({ ...m, ...p }));
  const patchOption = (i: number, p: Partial<RoleMenuOptionContent>) =>
    setMenu((m) => ({ ...m, options: m.options.map((o, idx) => (idx === i ? { ...o, ...p } : o)) }));
  const addOption = () => setMenu((m) => ({ ...m, options: [...m.options, { roleKey: "", label: "", removable: true }] }));
  const removeOption = (i: number) => setMenu((m) => ({ ...m, options: m.options.filter((_, idx) => idx !== i) }));

  return (
    <div className="ob-builder">
      <Card title="Menu basics">
        <div className="card-grid">
          <LabeledField label="Menu id" hint="A short stable id, e.g. pronouns.">
            <input className="field__input" value={menu.id} onChange={(e) => patch({ id: e.target.value })} aria-label="Menu id" />
          </LabeledField>
          <LabeledField label="Title">
            <input className="field__input" value={menu.title} onChange={(e) => patch({ title: e.target.value })} aria-label="Menu title" />
          </LabeledField>
          <LabeledField label="Target channel (resource key)" hint="From the blueprint, e.g. channel.roles.">
            <input className="field__input" value={menu.channelKey} onChange={(e) => patch({ channelKey: e.target.value })} aria-label="Target channel resource key" />
          </LabeledField>
          <LabeledField label="Menu type">
            <select className="field__input" value={menu.menuType} onChange={(e) => patch({ menuType: e.target.value as RoleMenuType })} aria-label="Menu type">
              {(Object.keys(ROLE_MENU_TYPE_LABEL) as RoleMenuType[]).map((t) => (
                <option key={t} value={t}>{ROLE_MENU_TYPE_LABEL[t]}</option>
              ))}
            </select>
          </LabeledField>
          <LabeledField label="Min selections">
            <input className="field__input" type="number" min={0} max={25} value={menu.minSelections ?? 0} onChange={(e) => patch({ minSelections: Number(e.target.value) })} aria-label="Minimum selections" />
          </LabeledField>
          <LabeledField label="Max selections">
            <input className="field__input" type="number" min={1} max={25} value={menu.maxSelections ?? 25} onChange={(e) => patch({ maxSelections: Number(e.target.value) })} aria-label="Maximum selections" />
          </LabeledField>
        </div>
        <label className="row" style={{ gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
          <input type="checkbox" checked={menu.requireVerified ?? false} onChange={(e) => patch({ requireVerified: e.target.checked })} />
          <span>Require the member to be verified before using this menu</span>
        </label>
        <LabeledField label="Description (optional)">
          <input className="field__input" value={menu.description ?? ""} onChange={(e) => patch({ description: e.target.value })} aria-label="Menu description" />
        </LabeledField>
      </Card>

      <Card title={`Options (${menu.options.length})`} description="Each option links a role resource key. ODIN assigns roles server-side behind a guard — no Discord role id is entered here.">
        {menu.options.map((o, i) => (
          <div key={i} className="ob-option-row">
            <input className="field__input" placeholder="Role resource key (e.g. role.artist)" value={o.roleKey} onChange={(e) => patchOption(i, { roleKey: e.target.value })} aria-label={`Option ${i + 1} role resource key`} />
            <input className="field__input" placeholder="Label" value={o.label} onChange={(e) => patchOption(i, { label: e.target.value })} aria-label={`Option ${i + 1} label`} />
            <input className="field__input" placeholder="Emoji (optional)" value={o.emoji ?? ""} onChange={(e) => patchOption(i, { emoji: e.target.value })} aria-label={`Option ${i + 1} emoji`} style={{ minWidth: 90 }} />
            <input className="field__input" placeholder="Exclusive group (optional)" value={o.exclusiveGroup ?? ""} onChange={(e) => patchOption(i, { exclusiveGroup: e.target.value })} aria-label={`Option ${i + 1} exclusive group`} />
            <label className="row" style={{ gap: "0.25rem", alignItems: "center" }}>
              <input type="checkbox" checked={o.removable ?? true} onChange={(e) => patchOption(i, { removable: e.target.checked })} aria-label={`Option ${i + 1} removable`} />
              <span className="muted">removable</span>
            </label>
            <button type="button" className="btn btn--ghost" onClick={() => removeOption(i)} aria-label={`Remove option ${i + 1}`}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn--secondary" onClick={addOption} style={{ marginTop: "0.5rem" }}>Add option</button>
      </Card>

      <Card title="Validation">
        <StatusIndicator tone={valid ? "ok" : "danger"} label={valid ? "Structurally valid" : `${blockers.length} blocker(s)`} />
        <FindingList
          findings={findings.map((f) => ({ severity: f.severity === "INFO" ? "info" : f.severity === "WARNING" ? "warning" : "blocker", message: `${f.message}${f.roleKey ? ` (${f.roleKey})` : ""}` }))}
          emptyLabel="No structural problems found."
        />
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          Structural checks run here in the browser. Privilege, managed-role and role-hierarchy checks run server-side against a snapshot when you validate — a self-assignable role can never carry privileged permissions.
        </p>
      </Card>

      <Card title="Preview">
        <RoleMenuPreview menu={menu} />
      </Card>

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={!valid || !onValidateRemote} onClick={() => onValidateRemote?.(menu)}>Validate against server roles</button>
        <button type="button" className="btn btn--secondary" disabled={!valid || !onGenerateDryRun} onClick={() => onGenerateDryRun?.(menu)}>Generate dry run</button>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem" }}>{NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED}</p>
    </div>
  );
}

// ── Message editor (structured — never raw payload/JSON) ─────────────────────
export function emptyMessage(): OnboardingMessageContent {
  return { plainText: "", title: "", description: "", footer: "", allowedMentions: { everyone: false, roles: [], users: false } };
}

export function MessageEditor({
  messageType,
  content,
  onChange,
  knownRoleKeys,
}: {
  messageType: ManagedMessageType;
  content: OnboardingMessageContent;
  onChange: (next: OnboardingMessageContent) => void;
  knownRoleKeys?: ReadonlySet<string>;
}): JSX.Element {
  const mentions = content.allowedMentions ?? { everyone: false, roles: [], users: false };
  const patch = (p: Partial<OnboardingMessageContent>) => onChange({ ...content, ...p });
  const patchMentions = (p: Partial<typeof mentions>) => patch({ allowedMentions: { ...mentions, ...p } });
  return (
    <div className="ob-editor">
      <div className="ob-editor__form">
        <LabeledField label="Plain text">
          <textarea className="field__input" rows={2} value={content.plainText ?? ""} onChange={(e) => patch({ plainText: e.target.value })} aria-label="Plain text" />
        </LabeledField>
        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <CharCount label="Text" value={content.plainText} limit={MESSAGE_LIMITS.plainText} />
        </div>
        <LabeledField label="Embed title">
          <input className="field__input" value={content.title ?? ""} onChange={(e) => patch({ title: e.target.value })} aria-label="Embed title" />
        </LabeledField>
        <LabeledField label="Embed description">
          <textarea className="field__input" rows={3} value={content.description ?? ""} onChange={(e) => patch({ description: e.target.value })} aria-label="Embed description" />
        </LabeledField>
        <LabeledField label="Footer">
          <input className="field__input" value={content.footer ?? ""} onChange={(e) => patch({ footer: e.target.value })} aria-label="Footer" />
        </LabeledField>
        <fieldset className="ob-editor__mentions">
          <legend>Allowed mentions</legend>
          <label className="row" style={{ gap: "0.4rem", alignItems: "center" }}>
            <input type="checkbox" checked={mentions.everyone} onChange={(e) => patchMentions({ everyone: e.target.checked })} aria-label="Allow @everyone / @here" />
            <span>Allow @everyone / @here</span>
          </label>
          <label className="row" style={{ gap: "0.4rem", alignItems: "center" }}>
            <input type="checkbox" checked={mentions.users} onChange={(e) => patchMentions({ users: e.target.checked })} aria-label="Allow user mentions" />
            <span>Allow user mentions</span>
          </label>
          <LabeledField label="Mentionable role keys (comma-separated)">
            <input
              className="field__input"
              value={mentions.roles.join(", ")}
              onChange={(e) => patchMentions({ roles: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              aria-label="Mentionable role keys"
            />
          </LabeledField>
        </fieldset>
      </div>
      <div className="ob-editor__preview">
        <MessagePreview messageType={messageType} content={content} knownRoleKeys={knownRoleKeys} />
      </div>
    </div>
  );
}

// ── The 15-step onboarding setup wizard ──────────────────────────────────────
export const WIZARD_STEPS = [
  "mode",
  "welcomeChannel",
  "rulesChannel",
  "verificationChannel",
  "verifiedRole",
  "welcomeContent",
  "rulesContent",
  "verificationContent",
  "acknowledgement",
  "manualReview",
  "previewNew",
  "previewVerified",
  "validate",
  "dryRun",
  "handoff",
] as const;
export type WizardStepId = (typeof WIZARD_STEPS)[number];

const WIZARD_STEP_LABEL: Record<WizardStepId, string> = {
  mode: "Mode",
  welcomeChannel: "Welcome channel",
  rulesChannel: "Rules channel",
  verificationChannel: "Verification channel",
  verifiedRole: "Verified role",
  welcomeContent: "Welcome content",
  rulesContent: "Rules content",
  verificationContent: "Verification content",
  acknowledgement: "Acknowledgement",
  manualReview: "Manual review",
  previewNew: "Preview: new member",
  previewVerified: "Preview: verified member",
  validate: "Validation",
  dryRun: "Dry run",
  handoff: "Deploy hand-off",
};

export function emptyOnboardingDraft(): OnboardingModuleContent {
  return {
    enabled: true,
    schemaVersion: "1",
    mode: "INFORMATION_ONLY",
    messages: {},
    behavior: { requireAcknowledgement: false, assignRoleOnAcknowledge: true, reAcknowledgeOnChange: false, dmWelcome: false, manualReviewExpiryHours: 72 },
  };
}

export interface WizardActions {
  createDraftAndValidate: (module: OnboardingModuleContent) => Promise<{ version: OnboardingVersionView; validation: OnboardingValidationOutcome }>;
  runDryRun: (blueprintVersionId: string) => Promise<OnboardingPlanPreview>;
}

export function OnboardingWizard({
  initialDraft,
  liveConfig,
  actions,
  releaseReviewHref = "/app/release-review",
}: {
  initialDraft?: OnboardingModuleContent;
  liveConfig?: OnboardingConfigResponse | null;
  actions?: WizardActions;
  releaseReviewHref?: string;
}): JSX.Element {
  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState<OnboardingModuleContent>(initialDraft ?? emptyOnboardingDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validated, setValidated] = useState<{ version: OnboardingVersionView; validation: OnboardingValidationOutcome } | null>(null);
  const [blueprintVersionId, setBlueprintVersionId] = useState("");
  const [preview, setPreview] = useState<OnboardingPlanPreview | null>(null);

  const step: WizardStepId = WIZARD_STEPS[stepIndex] ?? "mode";
  const setMode = (mode: OnboardingMode) => setDraft((d) => ({ ...d, mode }));
  const patch = (p: Partial<OnboardingModuleContent>) => setDraft((d) => ({ ...d, ...p }));
  const patchBehavior = (p: Partial<OnboardingBehavior>) => setDraft((d) => ({ ...d, behavior: { ...d.behavior, ...p } }));
  const setMessage = (slot: OnboardingMessageSlot, content: OnboardingMessageContent) =>
    setDraft((d) => ({ ...d, messages: { ...d.messages, [slot]: content } }));

  const knownRoleKeys = useMemo(() => {
    const s = new Set<string>();
    for (const k of [draft.verifiedRoleKey, draft.unverifiedRoleKey]) if (k) s.add(k);
    return s;
  }, [draft.verifiedRoleKey, draft.unverifiedRoleKey]);

  const usesManualReview = draft.mode === "MANUAL_APPROVAL" || draft.mode === "HYBRID";
  const showsVerification = draft.mode === "BUTTON_VERIFICATION" || draft.mode === "HYBRID" || draft.mode === "MANUAL_APPROVAL";
  const showsRules = draft.mode === "RULES_ACKNOWLEDGEMENT" || draft.mode === "HYBRID";

  const goNext = () => setStepIndex((i) => Math.min(i + 1, WIZARD_STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const runValidate = async () => {
    if (!actions) return;
    setBusy(true);
    setError(null);
    try {
      setValidated(await actions.createDraftAndValidate(draft));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Validation failed.");
    } finally {
      setBusy(false);
    }
  };

  const runDryRun = async () => {
    if (!actions || !blueprintVersionId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await actions.runDryRun(blueprintVersionId.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Dry run failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-wizard">
      {/* Persistent world-state strip: draft vs live/deployed — kept distinct. */}
      <div className="ob-worldstrip">
        <span className="state-tag state-tag--proposed" title="What you are drafting now">DRAFT — {MODE_META[draft.mode].label}</span>
        <span className="state-tag state-tag--current" title="What is live on Discord now">
          {liveConfig?.configuration?.enabled ? `LIVE — active version ${liveConfig.configuration.activeVersionId ?? "?"}` : "LIVE — no onboarding deployed"}
        </span>
        <span className="badge">{NO_DISCORD_CHANGES}</span>
      </div>

      <ol className="ob-steps" aria-label="Onboarding setup steps">
        {WIZARD_STEPS.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              className={`ob-step${i === stepIndex ? " ob-step--active" : ""}${i < stepIndex ? " ob-step--done" : ""}`}
              aria-current={i === stepIndex ? "step" : undefined}
              onClick={() => setStepIndex(i)}
            >
              {i + 1}. {WIZARD_STEP_LABEL[s]}
            </button>
          </li>
        ))}
      </ol>

      {error ? (
        <div className="state phase4-error" role="alert">
          <p className="state__title">Something needs your attention</p>
          <p>{error}</p>
        </div>
      ) : null}

      <Card title={`${stepIndex + 1}. ${WIZARD_STEP_LABEL[step]}`}>
        {step === "mode" ? (
          <div>
            <h2 className="section-title" style={{ marginTop: 0 }}>Choose an onboarding mode</h2>
            <p className="section-note">{VERIFICATION_FRAMING}</p>
            <div className="card-grid">
              {ONBOARDING_MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn ${draft.mode === m ? "btn--primary" : "btn--secondary"}`}
                  aria-pressed={draft.mode === m}
                  onClick={() => setMode(m)}
                  style={{ textAlign: "left", flexDirection: "column", alignItems: "flex-start" }}
                >
                  <strong>{MODE_META[m].label}</strong>
                  <span className="muted" style={{ fontSize: "var(--text-xs)" }}>{MODE_META[m].blurb}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {step === "welcomeChannel" ? (
          <ChannelStep label="Welcome channel resource key" hint="Where the welcome message is posted (e.g. channel.welcome)." value={draft.welcomeChannelKey} onChange={(v) => patch({ welcomeChannelKey: v })} />
        ) : null}
        {step === "rulesChannel" ? (
          <ChannelStep label="Rules channel resource key" hint={showsRules ? "Required for rules acknowledgement (e.g. channel.rules)." : "Optional for this mode."} value={draft.rulesChannelKey} onChange={(v) => patch({ rulesChannelKey: v })} />
        ) : null}
        {step === "verificationChannel" ? (
          <ChannelStep label="Verification channel resource key" hint={showsVerification ? "Where members confirm access (e.g. channel.verify)." : "Optional for this mode."} value={draft.verificationChannelKey} onChange={(v) => patch({ verificationChannelKey: v })} />
        ) : null}
        {step === "verifiedRole" ? (
          <div>
            <ChannelStep label="Verified-member role resource key" hint={showsVerification ? "Granted once a member is verified/approved (e.g. role.member)." : "Optional for this mode."} value={draft.verifiedRoleKey} onChange={(v) => patch({ verifiedRoleKey: v })} />
            <ChannelStep label="Unverified-member role resource key (optional)" hint="Held before verification, if you gate access with a role." value={draft.unverifiedRoleKey} onChange={(v) => patch({ unverifiedRoleKey: v })} />
            <p className="muted">ODIN never receives a Discord role id from your browser. Roles are assigned server-side, behind a guard, only to keys declared in your blueprint.</p>
          </div>
        ) : null}

        {step === "welcomeContent" ? (
          <MessageEditor messageType="WELCOME" content={draft.messages?.welcome ?? emptyMessage()} onChange={(c) => setMessage("welcome", c)} knownRoleKeys={knownRoleKeys} />
        ) : null}
        {step === "rulesContent" ? (
          <MessageEditor messageType="RULES" content={draft.messages?.rulesAck ?? emptyMessage()} onChange={(c) => setMessage("rulesAck", c)} knownRoleKeys={knownRoleKeys} />
        ) : null}
        {step === "verificationContent" ? (
          <MessageEditor messageType="VERIFICATION" content={draft.messages?.verificationPrompt ?? emptyMessage()} onChange={(c) => setMessage("verificationPrompt", c)} knownRoleKeys={knownRoleKeys} />
        ) : null}

        {step === "acknowledgement" ? (
          <div className="ob-toggles">
            <ToggleRow label="Require members to acknowledge before access" checked={draft.behavior?.requireAcknowledgement ?? false} onChange={(v) => patchBehavior({ requireAcknowledgement: v })} />
            <ToggleRow label="Assign the verified role when they acknowledge" checked={draft.behavior?.assignRoleOnAcknowledge ?? true} onChange={(v) => patchBehavior({ assignRoleOnAcknowledge: v })} />
            <ToggleRow label="Ask members to re-acknowledge when the rules change" checked={draft.behavior?.reAcknowledgeOnChange ?? false} onChange={(v) => patchBehavior({ reAcknowledgeOnChange: v })} />
            <ToggleRow label="Also DM the welcome message" checked={draft.behavior?.dmWelcome ?? false} onChange={(v) => patchBehavior({ dmWelcome: v })} />
            <p className="muted">Acknowledgement records that a member agreed to the rules and confirmed access. It is not identity verification.</p>
          </div>
        ) : null}

        {step === "manualReview" ? (
          <div>
            {usesManualReview ? (
              <LabeledField label="Manual-review expiry (hours)" hint="A pending review older than this expires. 1–720 hours.">
                <input className="field__input" type="number" min={1} max={720} value={draft.behavior?.manualReviewExpiryHours ?? 72} onChange={(e) => patchBehavior({ manualReviewExpiryHours: Number(e.target.value) })} aria-label="Manual-review expiry hours" />
              </LabeledField>
            ) : (
              <p className="muted">This mode does not use manual review. Choose Manual approval or Hybrid to add a human review step.</p>
            )}
          </div>
        ) : null}

        {step === "previewNew" ? (
          <div>
            <p className="section-note">How this looks to a <strong>new member</strong> before verification. {PREVIEW_DISCLAIMER}</p>
            {draft.messages?.welcome ? <MessagePreview messageType="WELCOME" content={draft.messages.welcome} knownRoleKeys={knownRoleKeys} /> : <p className="muted">No welcome content yet.</p>}
            {showsRules && draft.messages?.rulesAck ? <MessagePreview messageType="RULES" content={draft.messages.rulesAck} knownRoleKeys={knownRoleKeys} /> : null}
            {showsVerification && draft.messages?.verificationPrompt ? <MessagePreview messageType="VERIFICATION" content={draft.messages.verificationPrompt} knownRoleKeys={knownRoleKeys} /> : null}
          </div>
        ) : null}
        {step === "previewVerified" ? (
          <div>
            <p className="section-note">How this looks to an <strong>already-verified member</strong> — they would not see the verification prompt again. {PREVIEW_DISCLAIMER}</p>
            {draft.messages?.welcome ? <MessagePreview messageType="WELCOME" content={draft.messages.welcome} knownRoleKeys={knownRoleKeys} /> : <p className="muted">No welcome content yet.</p>}
          </div>
        ) : null}

        {step === "validate" ? (
          <div>
            <p className="section-note">Validate the draft against the blueprint schema. This creates a DRAFT version record only. {NO_DISCORD_CHANGES}</p>
            <button type="button" className="btn btn--primary" onClick={runValidate} disabled={busy || !actions}>
              {busy ? "Validating…" : "Create draft & validate"}
            </button>
            {!actions ? <p className="muted">Connect a workspace and server to validate.</p> : null}
            {validated ? (
              <div className="world-panel world-panel--desired" style={{ marginTop: "1rem" }}>
                <StatusIndicator tone={validated.validation.valid ? "ok" : "danger"} label={validated.validation.valid ? "Valid" : `${validated.validation.errors.length} blocker(s)`} />
                <p className="muted">Draft version id: <code>{validated.version.id}</code> (v{validated.version.version})</p>
                {validated.validation.errors.length > 0 ? (
                  <ul className="ob-findings">
                    {validated.validation.errors.map((e, i) => (
                      <li key={i} className="ob-finding ob-finding--danger"><span className="sev sev--danger">BLOCKER</span> {e.path}: {e.message}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === "dryRun" ? (
          <div>
            <p className="section-note">Generate a read-only dry run of the onboarding operations from a published blueprint version. {NO_DISCORD_CHANGES}</p>
            <div className="row" style={{ gap: "0.5rem" }}>
              <input className="field__input" placeholder="Blueprint version id" value={blueprintVersionId} onChange={(e) => setBlueprintVersionId(e.target.value)} aria-label="Blueprint version id" />
              <button type="button" className="btn btn--primary" onClick={runDryRun} disabled={busy || !actions || !blueprintVersionId.trim()}>
                {busy ? "Generating…" : "Generate dry run"}
              </button>
            </div>
            {preview ? (
              <div className="world-panel world-panel--proposed" style={{ marginTop: "1rem" }}>
                <StatusIndicator tone={preview.blocked ? "danger" : "ok"} label={preview.blocked ? `Blocked — ${preview.summary.blockers} blocker(s)` : `Ready — ${preview.operations.length} operation(s)`} />
                <div className="op-list" style={{ marginTop: "0.5rem" }}>
                  {preview.operations.map((op) => (
                    <div key={op.operationId} className="op-row">
                      <div className="op-row__head">
                        <span className="op-row__action">{op.operationType}</span>
                        {op.messageType ? <code className="op-row__key">{op.messageType}</code> : null}
                        {op.channelKey ? <code className="op-row__key">{op.channelKey}</code> : null}
                        {op.roleKey ? <code className="op-row__key">{op.roleKey}</code> : null}
                      </div>
                      <p className="op-row__reason">{op.reason}</p>
                    </div>
                  ))}
                </div>
                {preview.findings.length > 0 ? (
                  <FindingList findings={preview.findings.map((f) => ({ severity: f.severity === "BLOCKER" ? "blocker" : f.severity === "WARNING" ? "warning" : "info", message: `${f.message}${f.roleKey ? ` (${f.roleKey})` : ""}` }))} emptyLabel="" />
                ) : null}
              </div>
            ) : null}
            {!actions ? <p className="muted">Connect a workspace and server to generate a dry run.</p> : null}
          </div>
        ) : null}

        {step === "handoff" ? (
          <div className="ob-handoff">
            <h2 className="section-title" style={{ marginTop: 0 }}>Hand off to the Phase 4 approval flow</h2>
            <p>
              ODIN does not deploy onboarding from this wizard. The onboarding operations join the same immutable dry-run plan and travel the
              existing controlled-deployment pipeline. {HUMAN_APPROVAL_REQUIRED}
            </p>
            <ul className="ob-checklist">
              <li>Draft created &amp; validated {validated ? "✓" : "— not yet"}</li>
              <li>Dry run generated {preview ? "✓" : "— not yet"}</li>
              <li>Dry run not blocked {preview ? (preview.blocked ? "✗ — resolve blockers first" : "✓") : "— not yet"}</li>
            </ul>
            <a className="btn btn--primary" href={releaseReviewHref}>Open Phase 4 release review →</a>
            <p className="muted" style={{ marginTop: "0.75rem" }}>
              There is deliberately no "deploy" or "approve onboarding" button here. Activation only links a draft to a deployment that already
              cleared Phase 4 review and approval.
            </p>
          </div>
        ) : null}
      </Card>

      <div className="row" style={{ gap: "0.5rem", marginTop: "1rem" }}>
        <button type="button" className="btn btn--ghost" onClick={goBack} disabled={stepIndex === 0}>← Back</button>
        <button type="button" className="btn btn--secondary" onClick={goNext} disabled={stepIndex === WIZARD_STEPS.length - 1}>Next →</button>
      </div>
    </div>
  );
}

function ChannelStep({ label, hint, value, onChange }: { label: string; hint?: string; value: string | undefined; onChange: (v: string) => void }): JSX.Element {
  return (
    <LabeledField label={label} hint={hint}>
      <input className="field__input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} aria-label={label} placeholder="resource key from your blueprint" />
    </LabeledField>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }): JSX.Element {
  return (
    <label className="row" style={{ gap: "0.5rem", alignItems: "center", padding: "0.3rem 0" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
